import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ZfCheckbox,
  ZfFormField,
  ZfFormFieldAlert,
  ZfFormFieldLabel,
  ZfInput,
  ZfSelectOption,
  ZfStack,
  ZfTooltip,
} from '@zafire/react';
import {
  SafeDepositBoxSafeLocationOption,
  SafeDepositBoxSafeSizeOption,
  SafeLocation,
  SafeSize,
} from 'pco-product-service-internal-consumer';
import { ZfDividerTitle } from '../../../../../components/common/ZfDividerTitle';
import {
  SAFE_DEPOSIT_BOX_SAFE_NUMBER_MAX_LENGTH,
  SafeDepositBoxDetailsDraft,
  SafeDepositBoxDetailsErrors,
} from '../utils/safeDepositBoxDetailsDraft';

type Props = {
  details: SafeDepositBoxDetailsDraft;
  safeLocationOptions: SafeDepositBoxSafeLocationOption[];
  safeSizeOptions: SafeDepositBoxSafeSizeOption[];
  /** Safe location, safe size and safe number cannot be edited: the box is locked, loading or saving. */
  detailsReadOnly: boolean;
  /** The box has been activated, so the lock is permanent and worth explaining in a tooltip. */
  detailsLocked: boolean;
  /** Safe number specifically cannot be edited: either the same reasons as detailsReadOnly, or the
   * current user is not a CDS user - the safe number stays CDS-only regardless of activation. */
  safeNrReadOnly: boolean;
  canConfigureSafeDepositBoxDetails: boolean;
  keyDepositDisabled: boolean;
  errors: SafeDepositBoxDetailsErrors;
  onChange: (changes: Partial<SafeDepositBoxDetailsDraft>) => void;
  /** Backend-resolved: true if safe location and size can be edited. False when safe number
   * has been configured by CDS and current user is not CDS. */
  canEditSafeDepositBoxLocation: boolean;
};

const FIELD_STYLE = { flex: '1 1 200px' };

const firstSelectValue = (event: CustomEvent<string | string[]>) =>
  Array.isArray(event.detail) ? event.detail[0] : event.detail;

/**
 * Disabled controls cannot receive focus, so the reason for the lock is carried by a focusable group
 * around them. That group also anchors the tooltip, which makes it reachable by hover and by keyboard.
 */
const LockedFieldGroup: React.FC<{ testId: string; reason: string; children: React.ReactNode }> = ({
  testId,
  reason,
  children,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

  return (
    <div
      ref={setAnchorEl}
      data-testid={testId}
      role="group"
      aria-label={reason}
      tabIndex={0}
      style={{ ...FIELD_STYLE, pointerEvents: 'auto' }}
    >
      {children}
      {anchorEl && <ZfTooltip text={reason} anchor={() => anchorEl} side="top" />}
    </div>
  );
};

export const SafeDepositBoxConfigurationSection: React.FC<Props> = ({
  details,
  safeLocationOptions,
  safeSizeOptions,
  detailsReadOnly,
  detailsLocked,
  safeNrReadOnly,
  canConfigureSafeDepositBoxDetails,
  keyDepositDisabled,
  errors,
  onChange,
  canEditSafeDepositBoxLocation,
}) => {
  const { t } = useTranslation();

  const safeNrError =
    errors.safeNr === 'invalid'
      ? t('pb.configureproductmodal.safedepositbox.configuration.safenumber.invalid')
      : errors.safeNr === 'required'
        ? t('pb.configureproductmodal.safedepositbox.configuration.safenumber.required')
        : '';
  const safeLocationError =
    errors.safeLocation === 'required'
      ? t('pb.configureproductmodal.safedepositbox.configuration.safelocation.required')
      : '';
  const safeSizeError =
    errors.safeSize === 'required' ? t('pb.configureproductmodal.safedepositbox.configuration.safesize.required') : '';
  const lockedReason = t('pb.configureproductmodal.safedepositbox.configuration.locked.tooltip');
  const safeNumberCdsOnlyReason = t('pb.configureproductmodal.safedepositbox.configuration.safenumber.cdsonly.tooltip');
  const cdsConfiguredReason = t('pb.configureproductmodal.safedepositbox.configuration.cdsconfigured.tooltip');

  const withLock = (testId: string, locked: boolean, reason: string, field: React.ReactElement) =>
    locked ? (
      <LockedFieldGroup testId={testId} reason={reason}>
        {field}
      </LockedFieldGroup>
    ) : (
      field
    );

  const safeLocationField = (
    <ZfFormField style={FIELD_STYLE} error={!!safeLocationError} disabled={detailsReadOnly}>
      <ZfFormFieldLabel
        slot="label"
        htmlFor="safe-deposit-box-safe-location"
        mandatory={canConfigureSafeDepositBoxDetails}
        error={!!safeLocationError}
        text={t('pb.configureproductmodal.safedepositbox.configuration.safelocation.label')}
      />
      <ZfInput
        key={`safe-location-${safeLocationOptions.length}`}
        data-testid="safe-deposit-box-safe-location-select"
        id="safe-deposit-box-safe-location"
        name="safe-location"
        type="select"
        allowSearch
        mandatory={canConfigureSafeDepositBoxDetails}
        error={!!safeLocationError}
        value={details.safeLocation}
        disabled={detailsReadOnly}
        onSelectChange={(event: CustomEvent<string | string[]>) =>
          onChange({ safeLocation: (firstSelectValue(event) as SafeLocation) || '' })
        }
      >
        <ZfSelectOption value="" isSelected={!details.safeLocation}>
          {t('pb.configureproductmodal.safedepositbox.configuration.safelocation.placeholder')}
        </ZfSelectOption>
        {safeLocationOptions.map((option) => (
          <ZfSelectOption key={option.value} value={option.value} isSelected={details.safeLocation === option.value}>
            {option.label}
          </ZfSelectOption>
        ))}
      </ZfInput>
      <ZfFormFieldAlert slot="alert" id="safe-deposit-box-safe-location-alert" alerts={[safeLocationError]} />
    </ZfFormField>
  );

  const safeNrField = (
    <ZfFormField style={FIELD_STYLE} error={!!safeNrError} disabled={safeNrReadOnly}>
      <ZfFormFieldLabel
        slot="label"
        htmlFor="safe-deposit-box-safe-number"
        mandatory={canConfigureSafeDepositBoxDetails}
        error={!!safeNrError}
        text={t('pb.configureproductmodal.safedepositbox.configuration.safenumber.label')}
      />
      <ZfInput
        data-testid="safe-deposit-box-safe-number-input"
        id="safe-deposit-box-safe-number"
        name="safe-number"
        type="text"
        maxLength={SAFE_DEPOSIT_BOX_SAFE_NUMBER_MAX_LENGTH}
        mandatory={canConfigureSafeDepositBoxDetails}
        error={!!safeNrError}
        placeholder={t('pb.configureproductmodal.safedepositbox.configuration.safenumber.placeholder')}
        value={details.safeNr}
        disabled={safeNrReadOnly}
        onInputChange={(event: CustomEvent<string>) => onChange({ safeNr: event.detail })}
      />
      <ZfFormFieldAlert slot="alert" id="safe-deposit-box-safe-number-alert" alerts={[safeNrError]} />
    </ZfFormField>
  );

  const safeSizeField = (
    <ZfFormField style={FIELD_STYLE} error={!!safeSizeError} disabled={detailsReadOnly}>
      <ZfFormFieldLabel
        slot="label"
        htmlFor="safe-deposit-box-safe-size"
        mandatory={canConfigureSafeDepositBoxDetails}
        error={!!safeSizeError}
        text={t('pb.configureproductmodal.safedepositbox.configuration.safesize.label')}
      />
      <ZfInput
        key={`safe-size-${safeSizeOptions.length}`}
        data-testid="safe-deposit-box-safe-size-select"
        id="safe-deposit-box-safe-size"
        name="safe-size"
        type="select"
        mandatory={canConfigureSafeDepositBoxDetails}
        error={!!safeSizeError}
        value={details.safeSize}
        disabled={detailsReadOnly}
        onSelectChange={(event: CustomEvent<string | string[]>) =>
          onChange({ safeSize: (firstSelectValue(event) as SafeSize) || '' })
        }
      >
        <ZfSelectOption value="" isSelected={!details.safeSize}>
          {t('pb.configureproductmodal.safedepositbox.configuration.safesize.placeholder')}
        </ZfSelectOption>
        {safeSizeOptions.map((option) => (
          <ZfSelectOption key={option.value} value={option.value} isSelected={details.safeSize === option.value}>
            {option.label}
          </ZfSelectOption>
        ))}
      </ZfInput>
      <ZfFormFieldAlert slot="alert" id="safe-deposit-box-safe-size-alert" alerts={[safeSizeError]} />
    </ZfFormField>
  );

  return (
    <div data-testid="safe-deposit-box-configuration-section">
      <ZfDividerTitle label={t('pb.configureproductmodal.safedepositbox.configuration.title')} />

      <ZfStack direction="row" spacing="medium" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {withLock(
          'safe-deposit-box-safe-location-locked',
          detailsLocked || !canEditSafeDepositBoxLocation,
          detailsLocked ? lockedReason : cdsConfiguredReason,
          safeLocationField,
        )}
        {withLock(
          'safe-deposit-box-safe-number-locked',
          detailsLocked || !canConfigureSafeDepositBoxDetails,
          detailsLocked ? lockedReason : safeNumberCdsOnlyReason,
          safeNrField,
        )}
        {withLock(
          'safe-deposit-box-safe-size-locked',
          detailsLocked || !canEditSafeDepositBoxLocation,
          detailsLocked ? lockedReason : cdsConfiguredReason,
          safeSizeField,
        )}

        <ZfStack justify="flex-end" style={{ ...FIELD_STYLE, alignSelf: 'stretch' }}>
          <ZfCheckbox
            data-testid="safe-deposit-box-key-deposit-checkbox"
            name="key-deposit"
            label={t('pb.configureproductmodal.safedepositbox.configuration.keydeposit.label')}
            checked={details.keyDeposit}
            disabled={keyDepositDisabled}
            onCheckboxCheck={(event: CustomEvent<boolean>) => onChange({ keyDeposit: event.detail })}
          />
        </ZfStack>
      </ZfStack>
    </div>
  );
};
